/**
 * **What one client-management act means afterwards** — the sentence to say, and the caches that
 * stopped being true.
 *
 * MOVED here from `pages/client-roster/model/rowActionOutcome.ts` on 16 September 2026. It was the
 * roster's because the roster was the only surface that could end a coaching relationship; the
 * owner moved that act onto the client's own card ("te widoki sa read only praktycznie"), so there
 * are two consumers now and neither owns it. The alternative was a second copy of the
 * `session_in_progress` rule, and a domain rule that exists twice is a domain rule that will
 * disagree with itself.
 *
 * Nothing here knows about a table row: it takes an ACT and a server answer and returns a key plus
 * a list of caches. That is why it could move without dragging `PendingRowAction` along — that type
 * carries a `ClientRow`, and only the roster has rows.
 *
 * Pure: no React, no i18next. What leaves is a KEY the caller translates.
 */

export type RowAction = 'endCoaching' | 'cancelInvite' | 'resend';

/** The caches a write can invalidate. The page maps these onto `entities/client`'s query keys. */
export type RosterCache = 'roster' | 'seats' | 'archive';

export interface RowActionOutcome {
  /** The i18n key to announce. Always a key — no caller carries half of this contract. */
  readonly announceKey: string;
  readonly invalidates: readonly RosterCache[];
}

/**
 * `session_in_progress` — `Domain/ValidationErrorCodes.cs`. The trainer has to finish or cancel the
 * session they are running with this client before the relationship can end.
 */
const SESSION_IN_PROGRESS = 'session_in_progress';

const DONE_KEYS: Readonly<Record<RowAction, string>> = {
  endCoaching: 'web:clients.endCoaching.done',
  cancelInvite: 'web:clients.cancelInvite.done',
  resend: 'web:clients.resend.done',
};

const FAILED_KEYS: Readonly<Record<RowAction, string>> = {
  endCoaching: 'web:clients.endCoaching.failed',
  cancelInvite: 'web:clients.cancelInvite.failed',
  resend: 'web:clients.resend.failed',
};

/**
 * Which caches each act invalidates.
 *
 * · Ending a relationship removes the client from `GET /customer`, frees a seat immediately (the
 *   seat count reads `PersonalTrainerId`, which is nulled in the same transaction) and adds a row
 *   to `GET /customer/archived`. All three.
 * · Cancelling an invitation back-dates its expiry, so it leaves the roster and releases the seat
 *   it was holding. It never touches the archive — an invitation that was never accepted has no
 *   relationship to end.
 * · Resending an e-mail changes nothing a read can see.
 */
const INVALIDATIONS: Readonly<Record<RowAction, readonly RosterCache[]>> = {
  endCoaching: ['roster', 'seats', 'archive'],
  cancelInvite: ['roster', 'seats'],
  resend: [],
};

export function rowActionSucceeded(action: RowAction): RowActionOutcome {
  return { announceKey: DONE_KEYS[action], invalidates: INVALIDATIONS[action] };
}

/**
 * A refused write.
 *
 * Nothing is invalidated: the server rejected the act, so every cached read is still exactly as
 * true as it was. Refetching here would cost a request and change nothing, and it would hide the
 * refusal behind a spinner.
 */
export function rowActionFailed(
  action: RowAction,
  status: number,
  code: string | null | undefined,
): RowActionOutcome {
  if (action === 'endCoaching' && status === 400 && code === SESSION_IN_PROGRESS) {
    return { announceKey: 'web:clients.endCoaching.sessionInProgress', invalidates: [] };
  }
  return { announceKey: FAILED_KEYS[action], invalidates: [] };
}
